<script setup lang="ts">
import { computed } from 'vue';

type PropsType = {
    height?: number;
    showLegend?: boolean;
    showMetricSelector?: boolean;
    legendItems?: number;
};

const props = withDefaults(defineProps<PropsType>(), {
    height: 400,
    showLegend: true,
    showMetricSelector: true,
    legendItems: 5,
});

const chartAreaStyle = computed(() => ({ height: `${props.height}px` }));
</script>

<template>
    <div class="b-chart-skeleton">
        <div
            v-if="props.showLegend"
            class="b-chart-skeleton__legend-row"
        >
            <div class="b-chart-skeleton__legend">
                <div
                    v-for="n in props.legendItems"
                    :key="`legend-${n}`"
                    class="b-chart-skeleton__legend-item"
                >
                    <div class="b-chart-skeleton__legend-circle"></div>
                    <div class="b-chart-skeleton__legend-text"></div>
                </div>
            </div>

            <div
                v-if="props.showMetricSelector"
                class="b-chart-skeleton__metric-selector"
            ></div>
        </div>

        <div
            class="b-chart-skeleton__chart-area"
            :style="chartAreaStyle"
        >
            <div class="b-chart-skeleton__y-axis">
                <div
                    v-for="n in 6"
                    :key="`y-label-${n}`"
                    class="b-chart-skeleton__y-label"
                ></div>
            </div>

            <div class="b-chart-skeleton__canvas">
                <div class="b-chart-skeleton__grid">
                    <div
                        v-for="n in 5"
                        :key="`grid-h-${n}`"
                        class="b-chart-skeleton__grid-line-horizontal"
                    ></div>
                    <div
                        v-for="n in 7"
                        :key="`grid-v-${n}`"
                        class="b-chart-skeleton__grid-line-vertical"
                    ></div>
                </div>

                <div class="b-chart-skeleton__chart-lines">
                    <div
                        v-for="n in 3"
                        :key="`line-${n}`"
                        class="b-chart-skeleton__chart-line"
                        :style="{
                            animationDelay: `${n * 0.2}s`,
                            opacity: 1 - n * 0.2,
                        }"
                    ></div>
                </div>
            </div>

            <div class="b-chart-skeleton__x-axis">
                <div
                    v-for="n in 7"
                    :key="`x-label-${n}`"
                    class="b-chart-skeleton__x-label"
                ></div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@keyframes skeleton-pulse {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.4;
    }
    100% {
        opacity: 1;
    }
}

@keyframes skeleton-wave {
    0% {
        transform: translateX(-100%);
    }
    50% {
        transform: translateX(100%);
    }
    100% {
        transform: translateX(100%);
    }
}

.b-chart-skeleton {
    $skeleton-color: rgb(var(--v-theme-on-surface));
    $skeleton-bg: rgba(var(--v-theme-on-surface), 0.11);

    padding: 24px 0;

    &__legend-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        gap: 16px;
    }

    &__legend {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: center;
        flex: 1;
    }

    &__legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__legend-circle {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: $skeleton-bg;
        animation: skeleton-pulse 2s ease-in-out infinite;
    }

    &__legend-text {
        width: 80px;
        height: 12px;
        border-radius: 6px;
        background: $skeleton-bg;
        animation: skeleton-pulse 2s ease-in-out infinite;
        animation-delay: 0.1s;
    }

    &__metric-selector {
        width: 180px;
        height: 40px;
        border-radius: 6px;
        background: $skeleton-bg;
        animation: skeleton-pulse 2s ease-in-out infinite;
        animation-delay: 0.3s;
    }

    &__chart-area {
        position: relative;
        display: flex;
        border-radius: 8px;
        overflow: hidden;
        background: rgba(var(--v-theme-surface), 0.8);
        border: 1px solid rgba(var(--v-theme-outline), 0.12);
    }

    &__y-axis {
        width: 40px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 20px 8px 40px 8px;
    }

    &__y-label {
        width: 24px;
        height: 10px;
        background: $skeleton-bg;
        border-radius: 2px;
        animation: skeleton-pulse 2s ease-in-out infinite;

        &:nth-child(odd) {
            animation-delay: 0.1s;
        }
        &:nth-child(even) {
            animation-delay: 0.2s;
        }
    }

    &__canvas {
        flex: 1;
        position: relative;
        margin: 20px;
        margin-left: 0;
    }

    &__grid {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 40px;
    }

    &__grid-line-horizontal {
        position: absolute;
        left: 0;
        right: 0;
        height: 1px;
        background: rgba(var(--v-theme-outline), 0.06);

        @for $i from 1 through 5 {
            &:nth-child(#{$i}) {
                top: #{($i - 1) * 20%};
            }
        }
    }

    &__grid-line-vertical {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 1px;
        background: rgba(var(--v-theme-outline), 0.06);

        @for $i from 1 through 7 {
            &:nth-child(#{$i + 5}) {
                left: #{($i - 1) * 16.66%};
            }
        }
    }

    &__chart-lines {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 40px;
    }

    &__chart-line {
        position: absolute;
        top: 20%;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent, $skeleton-color 20%, $skeleton-color 80%, transparent);
        border-radius: 1px;
        animation: skeleton-wave 3s ease-in-out infinite;

        &:nth-child(1) {
            top: 20%;
        }
        &:nth-child(2) {
            top: 50%;
        }
        &:nth-child(3) {
            top: 70%;
        }
    }

    &__x-axis {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 40px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 20px;
    }

    &__x-label {
        width: 30px;
        height: 10px;
        background: $skeleton-bg;
        border-radius: 2px;
        animation: skeleton-pulse 2s ease-in-out infinite;

        &:nth-child(odd) {
            animation-delay: 0.1s;
        }
        &:nth-child(even) {
            animation-delay: 0.3s;
        }
    }
}

// Responsive adjustments
@media (max-width: 768px) {
    .b-chart-skeleton {
        &__legend-row {
            flex-direction: column;
            align-items: stretch;
        }

        &__legend {
            flex: none;
            justify-content: center;
        }

        &__metric-selector {
            width: 150px;
            align-self: flex-end;
        }
    }
}
</style>
